import React, { useEffect, useState } from 'react';
import {
  Drawer,
  DrawerBody,
  DrawerHeader,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  Box,
  VStack,
  HStack,
  Text,
  Button,
  Divider,
  useDisclosure,
  Badge,
  useToast,
  Flex,
  Progress,
  Icon,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalBody,
  ModalCloseButton,
  Image
} from '@chakra-ui/react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { logout } from '../../Redux/Login/action';
import { fetchUserProfile } from '../../Redux/Login/action';
import { openChatPanel } from '../../Redux/Chat/action';
import { FaCog, FaExclamationCircle, FaSignOutAlt, FaCoins, FaUsers, FaTicketAlt, FaComments, FaCreditCard, FaNewspaper, FaHome } from 'react-icons/fa';
import MembershipSettings from './MembershipSettings';
import ReportIssue from './ReportIssue';
import MembershipModal from './MembershipModal';
import { isUserAdmin } from '../../utils/authHelper';
import BurrowAvatar from '../UI/BurrowAvatar';
import { LEVEL_RANKS, RESPECT_RANKS, computeRank } from '../../utils/burrowRanks';
import api from '../../services/api';


const UserDrawer = ({ isOpen, onClose, communityModalOpen, setCommunityModalOpen }) => {
  const toast = useToast();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const authState = useSelector(state => state.auth);
  const user = authState?.user || {};
  const profile = authState?.profile || {};
  const burrowPoints = profile.burrowPoints || 0;
  const [liveRespect, setLiveRespect] = useState(profile.respectCount || 0);
  const respectCount = liveRespect; // refreshed live each time the drawer opens
  const lvl = computeRank(burrowPoints, LEVEL_RANKS);
  const rsp = computeRank(respectCount, RESPECT_RANKS);

  // Live respect headcount — same source as the dashboard (drawer badge + stars must match)
  const meId = user.burrowerId || profile.burrowerId;
  useEffect(() => {
    if (!isOpen || !meId) return;
    let live = true;
    api.get(`/api/user/profile/${encodeURIComponent(meId)}`, { cache: false })
      .then(r => r.data)
      .then(d => {
        const rc = d?.profile?.respectCount;
        if (live && typeof rc === 'number') setLiveRespect(rc);
      })
      .catch(() => {});
    return () => { live = false; };
  }, [isOpen, meId]);

  // Debug navigate function
  console.log('UserDrawer - navigate function initialized:', !!navigate);

  // Disclosures for child components
  const settingsModal = useDisclosure();
  const reportModal = useDisclosure();
  const avatarPreviewModal = useDisclosure();
  const membershipModal = useDisclosure();

  // Fetch user profile data only if profile is stale (not on every open)
  // This improves performance by avoiding redundant API calls
  useEffect(() => {
    if (isOpen && authState.isAuth) {
      console.group('UserDrawer State');
      console.log('Opening drawer for user:', user.burrowerId);
      console.log('Current user level:', profile.accountLevel);
      console.log('Account type:', profile.accountType);
      console.groupEnd();

      // Only fetch if profile data is missing or very stale
      const hasProfileData = profile.burrowerId && profile.accountLevel;
      if (!hasProfileData) {
        console.log('Profile data missing, fetching from server...');
        dispatch(fetchUserProfile());
      }
    }
  }, [isOpen, authState.isAuth, dispatch, user.burrowerId, profile.burrowerId, profile.accountLevel]);



  const handleLogout = () => {
    dispatch(logout());
    onClose();

    toast({
      title: "Logged out successfully",
      status: "success",
      duration: 1500,
      isClosable: true,
    });
  };

  // Add a function to handle opening the community modal
  const handleOpenCommunity = () => {
    // Close the drawer first
    onClose();

    // Then open the community modal with a small delay
    setTimeout(() => {
      setCommunityModalOpen(true);
    }, 300);
  };

  // Helper function to verify auth state is properly synced
  const verifyAuthStateAndPermissions = () => {
    const token = localStorage.getItem("burrowtoken");
    const userData = localStorage.getItem("burrowUserData");
    const reduxIsAuth = authState?.isAuth;
    const reduxHasUser = Boolean(authState?.user);

    console.log('Auth state verification:', {
      hasLocalToken: Boolean(token),
      hasLocalUserData: Boolean(userData),
      reduxIsAuth,
      reduxHasUser,
      userAccountType: user.accountType,
      userRole: user.role,
      isAdmin: isUserAdmin(user)
    });

    return {
      isAuthenticated: Boolean(token) && reduxIsAuth && reduxHasUser,
      isAdmin: isUserAdmin(user)
    };
  };
    // Function to handle navigation to help requests/tickets
  const handleHelpRequests = () => {
    // Verify auth state before proceeding
    const authCheck = verifyAuthStateAndPermissions();

    if (!authCheck.isAuthenticated) {
      console.error('User is not properly authenticated');
      toast({
        title: "Authentication required",
        description: "Please log in to access help requests",
        status: "error",
        duration: 3000,
        isClosable: true
      });

      // Close drawer and redirect to home
      onClose();
      setTimeout(() => navigate('/'), 100);
      return;
    }

    // Store the target path first
    const targetPath = authCheck.isAdmin ? '/admin/inbox' : '/tickets';

    console.log('Help Requests clicked, navigating to:', targetPath);

    // Use a more robust approach to navigation
    const performNavigation = () => {
      // Execute navigation in try-catch to debug any errors
      try {
        // Use window.location for a full page navigation as a fallback approach
        // This bypasses any potential router issues
        window.location.href = targetPath;
      } catch (error) {
        console.error('Navigation error:', error);
        // Try regular navigation as fallback
        navigate(targetPath, { replace: true });
      }
    };

    // Close the drawer first
    onClose();

    // Use a longer delay to ensure drawer is fully closed and any animations complete
    setTimeout(performNavigation, 300);
  };

  // Function to handle navigation to chat interface
  const handleChatNavigation = () => {
    // Verify auth state before proceeding
    const authCheck = verifyAuthStateAndPermissions();

    if (!authCheck.isAuthenticated) {
      console.error('User is not properly authenticated');
      toast({
        title: "Authentication required",
        description: "Please log in to access chat",
        status: "error",
        duration: 3000,
        isClosable: true
      });

      // Close drawer and redirect to home
      onClose();
      setTimeout(() => navigate('/'), 100);
      return;
    }

    // Target path for chat interface
    const targetPath = '/chat';

    console.log('Chat clicked, navigating to:', targetPath);

    // Close the drawer first
    onClose();

    // Desktop (>=768px) → open floating panel without navigating
    if (window.innerWidth >= 768) {
      setTimeout(() => dispatch(openChatPanel()), 300);
      return;
    }

    // Mobile → navigate to full-page chat
    const performNavigation = () => {
      try {
        navigate(targetPath);
      } catch (error) {
        console.error('Navigation error:', error);
        navigate(targetPath, { replace: true });
      }
    };

    // Use a longer delay to ensure drawer is fully closed and any animations complete
    setTimeout(performNavigation, 300);
  };

  return (
    <React.Fragment>
      <Drawer isOpen={isOpen} onClose={onClose} placement="right" size="md">
        <DrawerOverlay />
        <DrawerContent bg="gray.800" color="white">
          <DrawerCloseButton />
          <DrawerHeader borderBottomWidth="1px" borderColor="gray.700" pr={16}>
            <Flex justify="space-between" align="center">
              <Text fontWeight="bold" fontSize="lg">My Burrow Profile</Text>
              <Button
                size="sm"
                variant="ghost"
                leftIcon={<FaSignOutAlt color="#E53E3E" />}
                onClick={handleLogout}
                color="red.300"
                _hover={{ bg: "gray.700", color: "red.200" }}
                mr={1}
              >
                Logout
              </Button>
            </Flex>
          </DrawerHeader>

          <DrawerBody py={4}>
            <VStack spacing={6} align="stretch">
              {/* Profile Header */}
              <Box
                bg="gray.700"
                borderRadius="md"
                p={4}
                position="relative"
                overflow="hidden"
              >
                <Box
                  position="absolute"
                  top={0}
                  left={0}
                  right={0}
                  height="60px"
                  bgGradient="linear(to-r, yellow.600, yellow.300, yellow.600,)"
                  opacity={0.6}
                />

                <VStack spacing={3} position="relative">                  <Box
                    cursor="pointer"
                    borderRadius="full"
                    transition="transform 0.2s"
                    _hover={{ transform: "scale(1.05)" }}
                    onClick={avatarPreviewModal.onOpen}
                    position="relative"
                    role="group"
                  >
                    <BurrowAvatar
                      size="xl"
                      name={user.burrowerId || "User"}
                      src={profile.userAvatar}
                      border="3px solid"
                      borderColor="yellow.400"
                      boxShadow="0 0 15px rgba(236, 201, 75, 0.5)"
                    />
                    <Box
                      position="absolute"
                      bottom="-5px"
                      left="50%"
                      transform="translateX(-50%)"
                      bg="blackAlpha.700"
                      color="white"
                      fontSize="xs"
                      px={2}
                      py={1}
                      borderRadius="md"
                      opacity="0"
                      _groupHover={{ opacity: "1" }}
                      transition="opacity 0.2s"
                      pointerEvents="none"
                      width="auto"
                      whiteSpace="nowrap"
                    >
                      Click to enlarge
                    </Box>
                  </Box>

                  <VStack spacing={1}>
                    <Text fontSize="xl" fontWeight="bold">{user.burrowerId || "User"}</Text>

                    <HStack spacing={1.5} flexWrap="wrap" justify="center">
                      <Badge colorScheme={user.accountType === 'premium' ? "purple" : "gray"} px={2} py={1}>
                        {user.accountType === 'admin' ? "Admin" : user.accountType === 'premium' ? "Premium" : "Free"}
                      </Badge>
                      <Badge bg="whiteAlpha.200" color="white" px={2} py={1} title="Respect status">{rsp.rank.icon} {rsp.rank.name}</Badge>
                      <Badge bg="whiteAlpha.200" color="white" px={2} py={1} title="Burrow Level">{lvl.rank.icon} {lvl.rank.name}</Badge>
                    </HStack>

                    {/* Respect stars — lit per person who respects you (0–5) */}
                    <Text fontSize="17px" color="yellow.400" letterSpacing="3px" lineHeight="1" aria-label={`${Math.min(respectCount, 5)} of 5 respect`}>
                      {'★'.repeat(Math.min(respectCount, 5))}{'☆'.repeat(5 - Math.min(respectCount, 5))}
                    </Text>
                  </VStack>
                </VStack>

                {/* XP Progress */}
                <Box mt={4}>
                  <Flex align="center" mb={1}>
                    <Text flex="1" textAlign="left" fontSize="xs">{lvl.next ? `Next: ${lvl.next.icon} ${lvl.next.name}` : `Top rank — ${lvl.rank.name}`}</Text>
                    <HStack flex="1" justify="center" spacing={1}>
                      <Icon as={FaCoins} color="yellow.400" />
                      <Text fontSize="xs">{profile.accountCredits || 0} MU</Text>
                    </HStack>
                    <Text flex="1" textAlign="right" fontSize="xs">{lvl.next ? `${burrowPoints} / ${lvl.next.threshold.toLocaleString()} BP` : `${burrowPoints} BP`}</Text>
                  </Flex>
                  <Progress
                    value={lvl.pct}
                    size="sm"
                    sx={{
                      "& > div:first-of-type": {
                        background: "linear-gradient(90deg,rgb(180, 154, 4),rgb(206, 187, 21),rgb(223, 190, 8))",
                        transition: "box-shadow 0.3s",
                        _hover: {
                          boxShadow: "0 0 10px #ffd700, 0 0 20px #ffd700",
                        }
                      }
                    }}
                    borderRadius="full"
                  />
                </Box>
              </Box>

              <Divider borderColor="gray.600" />
                {/* Action Buttons */}
              <VStack spacing={3} align="stretch">                {/* Order adjusted as requested */}                {/* Dashboard Button */}
                <Button
                  leftIcon={<FaHome color="#E3B341" />}
                  justifyContent="flex-start"
                  variant="solid"
                  bg="gray.700"
                  color="white"
                  onClick={() => { navigate('/dashboard'); onClose(); }}
                  size="md"
                  height="48px"
                  fontSize="md"
                  _hover={{ transform: "translateY(-2px)", shadow: "md", bg: "gray.600" }}
                  transition="all 0.2s"
                >
                  Dashboard
                </Button>

                <Divider borderColor="gray.600" />

                {/* Community Button - updated to match style */}                <Button
                  leftIcon={<FaUsers color="#63B3ED" />}
                  justifyContent="flex-start"
                  variant="solid"
                  bg="gray.700"
                  color="white"
                  onClick={handleOpenCommunity}
                  size="md"
                  height="48px"
                  fontSize="md"
                  _hover={{ transform: "translateY(-2px)", shadow: "md", bg: "gray.600" }}
                  transition="all 0.2s"
                >
                  Community
                </Button>

                {/* Chat Button - placed below Community button */}
                <Button
                  leftIcon={<FaComments color="#4FD1C5" />}
                  justifyContent="flex-start"
                  variant="solid"
                  bg="gray.700"
                  color="white"
                  onClick={handleChatNavigation}
                  size="md"
                  height="48px"
                  fontSize="md"
                  _hover={{ transform: "translateY(-2px)", shadow: "md", bg: "gray.600" }}
                  transition="all 0.2s"
                >
                  Chat
                </Button>

                {/* The Feed Button */}
                <Button
                  leftIcon={<FaNewspaper color="#F6E05E" />}
                  justifyContent="flex-start"
                  variant="solid"
                  bg="gray.700"
                  color="white"
                  onClick={() => { navigate('/feed'); onClose(); }}
                  size="md"
                  height="48px"
                  fontSize="md"
                  _hover={{ transform: "translateY(-2px)", shadow: "md", bg: "gray.600" }}
                  transition="all 0.2s"
                >
                  The Feed
                </Button>

                {/* Divider */}
                <Divider borderColor="gray.600" />

                {/* Manage Membership Button */}
                <Button
                  leftIcon={<FaCreditCard color="#F6AD55" />}
                  justifyContent="flex-start"
                  variant="solid"
                  bg="gray.700"
                  color="white"
                  onClick={membershipModal.onOpen}
                  size="md"
                  height="48px"
                  fontSize="md"
                  _hover={{ transform: "translateY(-2px)", shadow: "md", bg: "gray.600" }}
                  transition="all 0.2s"
                >
                  Manage Membership
                </Button>

                <Divider borderColor="gray.600" />

                {/* Request Help Button */}
                <Button
                  leftIcon={<FaTicketAlt color="#63B3ED" />}
                  justifyContent="flex-start"
                  variant="solid"
                  bg="gray.700"
                  color="white"
                  onClick={handleHelpRequests}
                  size="md"
                  height="48px"
                  fontSize="md"
                  _hover={{ transform: "translateY(-2px)", shadow: "md", bg: "gray.600" }}
                  transition="all 0.2s"
                >
                  Request Help
                </Button>
                  {/* Feedback Button - renamed from "Feedback" to "Give Feedback" */}
                <Button
                  leftIcon={<FaExclamationCircle color="#48BB78" />}
                  justifyContent="flex-start"
                  variant="solid"
                  bg="gray.700"
                  color="white"
                  onClick={reportModal.onOpen}
                  size="md"
                  height="48px"
                  fontSize="md"
                  _hover={{ transform: "translateY(-2px)", shadow: "md", bg: "gray.600" }}
                  transition="all 0.2s"
                >
                  Give Feedback
                </Button>

                {/* Divider to isolate Membership Settings */}
                <Divider borderColor="gray.600" />

                {/* Membership Settings Button */}
                <Button
                  leftIcon={<FaCog color="#63B3ED" />}
                  justifyContent="flex-start"
                  variant="solid"
                  bg="gray.700"
                  color="white"
                  onClick={settingsModal.onOpen}
                  size="md"
                  height="48px"
                  fontSize="md"
                  _hover={{ transform: "translateY(-2px)", shadow: "md", bg: "gray.600" }}
                  transition="all 0.2s"
                >
                  Membership Settings
                </Button>
              </VStack>


            </VStack>
          </DrawerBody>
        </DrawerContent>
      </Drawer>

      {/* Modals */}
      <MembershipSettings
        isOpen={settingsModal.isOpen}
        onClose={settingsModal.onClose}
      />

      <ReportIssue
        isOpen={reportModal.isOpen}
        onClose={reportModal.onClose}
      />

      <MembershipModal
        isOpen={membershipModal.isOpen}
        onClose={membershipModal.onClose}
      />

      {/* Avatar Preview Modal */}
      <Modal
        isOpen={avatarPreviewModal.isOpen}
        onClose={avatarPreviewModal.onClose}
        size="3xl"
        motionPreset="slideInBottom"
        isCentered
      >
        <ModalOverlay
          bg="blackAlpha.700"
          backdropFilter="blur(5px)"
        />
        <ModalContent bg="transparent" shadow="none" maxW="90vw">
          <ModalCloseButton
            color="white"
            bg="blackAlpha.700"
            borderRadius="full"
            size="lg"
            _hover={{ bg: "blackAlpha.800" }}
          />
          <ModalBody display="flex" justifyContent="center" alignItems="center" p={8}>
            <Box
              p={6}
              borderRadius="xl"
              bg="gray.800"
              borderWidth="3px"
              borderColor="yellow.400"
              overflow="hidden"
              shadow="2xl"
              maxW="95%"
              transition="all 0.3s"
              position="relative"
              _before={{
                content: '""',
                position: "absolute",
                inset: "0",
                bgGradient: "linear(to-r, yellow.400, orange.400, yellow.500)",
                opacity: "0.1",
                borderRadius: "xl"
              }}
              animation="pulse 2s infinite"
              sx={{
                "@keyframes pulse": {
                  "0%": { boxShadow: "0 0 0 0 rgba(236, 201, 75, 0.5)" },
                  "70%": { boxShadow: "0 0 0 15px rgba(236, 201, 75, 0)" },
                  "100%": { boxShadow: "0 0 0 0 rgba(236, 201, 75, 0)" }
                }
              }}
            >
              <Image
                src={profile.userAvatar || "https://bunny-burrow-pull-1.b-cdn.net/user-profile-images/image%20(16).png"}
                alt={user.burrowerId || "User"}
                borderRadius="lg"
                maxH="80vh"
                minH="400px"
                minW="400px"
                objectFit="contain"
                boxShadow="lg"
              />
            </Box>
          </ModalBody>
        </ModalContent>
      </Modal>
    </React.Fragment>
  );
};

export default UserDrawer;
